import React, { useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import OrderProp from '../../propTypes/orderProp';
import translate from '../../../../commonComponents/translate';
import SummaryItem from './SummaryItem';
import {
  displayNumber,
  findCurrencySymbol,
  formatNumbers,
  formatPrice,
} from '../../../../services/utilityFunctions';
import WorldContext from '../../../../contextProviders/WorldContext';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import User from '../../../../../../../domain/models/user';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import { normalizeNumber } from '../../../../services/numberUtilis';

function OrderSummary(props) {
  const { t, order, isAcademy, isOpportunity, user } = props;
  const { currencies } = useContext(WorldContext);
  const { tenant } = useContext(ConfigsContext);
  const { directSellingSettings, directSelling } = tenant?.settings;
  const { CVIntegration, VAIntegration } = directSellingSettings || {};
  const isOwnerACollaborator =
    user?.type === User.TYPE.COLLABORATOR || user?.role === User.ROLE.ADMIN;

  const showVA = directSelling && VAIntegration && isOwnerACollaborator;
  const showCV = directSelling && CVIntegration && isOwnerACollaborator;

  const {
    subtotal,
    currency,
    discountTotal,
    paymentCoins,
    isPaidWithCoins,
    pricesWithoutVAT,
    userDiscountTotal,
    userDiscount,
    fee,
    subtotalTax,
    shippingTax,
    shippingTotal,
    total,
    totalVA,
    refund,
    refundVA,
    totalCV,
    invoice,
    refundCV,
  } = order || {};
  const { feePercentage, feeSubTotal } = fee || {};

  const orderCurrencySymbol = findCurrencySymbol(currency, currencies);

  const vatInfo = pricesWithoutVAT
    ? ` (${t('checkout.payment.vatExcl')})`
    : ` (${t('checkout.payment.vatInc')})`;

  // region Subtotal
  const subtotalLabel = `${t('shop.order_items_subtotal')} (${t(
    'checkout.payment.vatExcl',
  )}):`;
  const subtotalValue = formatPrice(subtotal, orderCurrencySymbol);
  // endregion Subtotal

  // region Discount total
  const discountLabel = isPaidWithCoins
    ? `${t('shop.checkout.coupon.coins')} (${
        paymentCoins?.coins && paymentCoins.coins >= 0
          ? displayNumber(paymentCoins.coins)
          : 0
      })`
    : t('shop.order_discount_coupon');
  const discountFinalLabel = `${discountLabel}${
    order.isPaidWithCoins ? '' : vatInfo
  }:`;
  const discountValue = `- ${formatPrice(
    order.discountTotal,
    orderCurrencySymbol,
  )}`;
  // endregion Discount total

  // region User Discount
  const existUserDiscount = !isAcademy && userDiscountTotal > 0;
  const userDiscountLabel = `${t(
    'order_discount',
  )} (${userDiscount}%) ${vatInfo}:`;
  const userDiscountValue = `- ${formatPrice(
    userDiscountTotal,
    orderCurrencySymbol,
  )}`;
  // endregion User Discount

  // region Fee
  const existFee = feeSubTotal > 0;
  const feeLabel = `${t('order.fee', `${feePercentage}%`, subtotalValue)} (${t(
    'checkout.payment.vatExcl',
  )}):`;
  const feeValue = formatPrice(feeSubTotal, orderCurrencySymbol);
  // endregion Fee

  // region VAT
  const vatLabel = `${t('shop.vat')}:`;
  const vatValue = formatPrice(subtotalTax, orderCurrencySymbol);
  // endregion VAT

  const subtotalExtraTax = useMemo(
    () => ({
      label: `${t('order.extraTax')}:`,
      value: formatPrice(order.subtotalExtraTax, orderCurrencySymbol),
      show: order.subtotalExtraTax > 0,
    }),
    [order.subtotalExtraTax, orderCurrencySymbol, t],
  );

  // region Shipping
  const shippingLabel = `${t('shop.order_shipping_cost')} (${shippingTax}%):`;
  const shippingValue = formatPrice(shippingTotal, orderCurrencySymbol);
  // endregion Shipping

  // region Total
  const totalLabel = isOpportunity
    ? `${t('opportunity.total_price')}:`
    : `${t('shop.order_total_price')}:`;
  const totalValue = formatPrice(total, orderCurrencySymbol);
  // endregion Total

  // region Total VA
  const totalVALabel = `${t('shop.totalVA')}:`;
  const totalVAValue = totalVA !== null ? normalizeNumber(totalVA, 2) : 0;
  // endregion Total VA

  // region CV
  const cvLabel = `${t('shop.totalCV')}:`;
  const cvValue = totalCV !== null ? normalizeNumber(totalCV, 2) : 0;
  // endregion CV

  // region Refund
  const showRefund = !isAcademy && refund > 0;
  const refundLabel = `${t('order.refund_already_refunded')}:`;
  const refundValue = formatPrice(refund, orderCurrencySymbol);
  // endregion Refund

  // region Refund VA Value
  const showRefundVA = !isAcademy && showVA && refundVA > 0;
  const refundVALabel = `${t('order.refund_already_refunded.va')}:`;
  const refundVAValue = normalizeNumber(refundVA, 2);
  // endregion Refund VA Value

  // region Refund CV Value
  const showRefundCV = !isAcademy && showCV && refundCV > 0;
  const refundCVLabel = `${t('order.refund_already_refunded.cv')}:`;
  const refundCVValue = normalizeNumber(refundCV, 2);
  // endregion Refund VA Value

  const invoiceLink = `/shop/invoice/${order._id}?type=${
    isAcademy ? 'academy' : 'market'
  }`;

  return (
    <div className="order-summary">
      <SummaryItem title={subtotalLabel} text={subtotalValue} />
      {discountTotal ? (
        <SummaryItem title={discountFinalLabel} text={discountValue} />
      ) : null}
      {existUserDiscount ? (
        <SummaryItem title={userDiscountLabel} text={userDiscountValue} />
      ) : null}
      {existFee ? <SummaryItem title={feeLabel} text={feeValue} /> : null}
      <SummaryItem title={vatLabel} text={vatValue} />
      {subtotalExtraTax?.show && (
        <SummaryItem
          title={subtotalExtraTax.label}
          text={subtotalExtraTax.value}
        />
      )}
      {!isAcademy ? (
        <SummaryItem title={shippingLabel} text={shippingValue} />
      ) : null}
      <SummaryItem title={totalLabel} text={totalValue} />
      {showVA ? <SummaryItem title={totalVALabel} text={totalVAValue} /> : null}
      {showCV ? <SummaryItem title={cvLabel} text={cvValue} /> : null}
      {showRefund ? (
        <SummaryItem title={refundLabel} text={refundValue} />
      ) : null}
      {showRefundVA ? (
        <SummaryItem title={refundVALabel} text={refundVAValue} />
      ) : null}
      {showRefundCV ? (
        <SummaryItem title={refundCVLabel} text={refundCVValue} />
      ) : null}
      {invoice?.number ? (
        <div className="download-invoice-wrapper">
          <a
            target="_blank"
            className="btn btn-dynamic m-btn--pill download-invoice"
            rel="noreferrer"
            href={invoiceLink}
          >
            {t('shop.order_download')}
          </a>
        </div>
      ) : null}
    </div>
  );
}

OrderSummary.propTypes = {
  t: PropTypes.func.isRequired,
  order: PropTypes.shape(OrderProp.propType).isRequired,
  isAcademy: PropTypes.bool,
  isOpportunity: PropTypes.bool,
  user: PropTypes.shape(UserProp.propType).isRequired,
};

OrderSummary.defaultProps = {
  isAcademy: false,
  isOpportunity: false,
};

export default translate(OrderSummary);
